import DayPlanTrackingActionButton from "features/CenterContent/RoleContent/Personellstyrer/DayPlanTrackings/TableContent/ActionButton";
import { getStatus } from "features/CenterContent/RoleContent/Personellstyrer/DayPlanTrackings/TableContent/utils";
import { DayPlanTrackingType } from "features/CenterContent/RoleContent/Personellstyrer/DayPlanTrackings/types";
import { getPhoneNumberWithoutNOPrefix } from "features/Header/PuzzelTelephone/utils";
import { ComponentProps, FC } from "react";
import { CallButton } from "shared/components/buttons/CallButton";
import { NoDataMessage } from "shared/components/feedback/NoDataMessage/NoDataMessage";
import { Box, Grid, Stack, Text } from "shared/components/Spor";
import { formatDayDateString } from "shared/utils/dateTimeUtils";
import { css } from "styled-components";

const getBackgroundColor = (
  severity: string,
): ComponentProps<typeof Grid>["color"] => {
  switch (severity) {
    case "WARN":
      return "semantics.colorWarningFill";
    case "ERROR":
      return "semantics.colorAlarmFill";
    case "OK":
      return "semantics.colorSuccessFill";
    default:
      return "inherit";
  }
};

const getHoverBackgroundColor = (
  severity: string,
): ComponentProps<typeof Grid>["color"] => {
  switch (severity) {
    case "WARN":
      return "semantics.colorWarningFillPulse";
    case "ERROR":
      return "semantics.colorAlarmFillPulse";
    case "OK":
      return "semantics.colorSuccessFillPulse";
    default:
      return "inherit";
  }
};

type TableProps = {
  filteredDayPlanTrackings: DayPlanTrackingType[];
  onRowClick: (dayPlanTrackingType: DayPlanTrackingType) => void;
};

export const TableContent: FC<TableProps> = ({
  filteredDayPlanTrackings,
  onRowClick,
}) =>
  filteredDayPlanTrackings.length > 0 ? (
    filteredDayPlanTrackings.map((it, index) => {
      const status = getStatus(it.status);
      const backgroundColor = getBackgroundColor(status.severity);
      const hoverBackgroundColor = getHoverBackgroundColor(status.severity);
      return (
        <Grid
          key={`${it.employeeId}-${it.dayPlanDate}`}
          cursor="pointer"
          gridColumn="1 / -1"
          gridTemplateColumns="subgrid"
          backgroundColor={backgroundColor}
          _hover={{ backgroundColor: hoverBackgroundColor }}
          border="1px solid"
          borderColor="outline.disabled"
          borderTop="none"
          borderBottomRadius={
            index === filteredDayPlanTrackings.length - 1 ? "xs" : undefined
          }
          transition="all 200ms ease-in-out"
          onClick={() => onRowClick(it)}
          gap={6}
          px={3}
          py={1.5}
          alignItems="center"
        >
          <Text variant="xs">{status.text}</Text>
          <Text>{it.companyCode}</Text>
          <Text>{formatDayDateString(it.lastUpdatedAt)}</Text>
          <Text>{formatDayDateString(it.dutyStart)}</Text>{" "}
          <Text>
            {it.fullName ? `${it.fullName} (${it.employeeId})` : it.employeeId}
          </Text>
          <Text>{`${it.isConductor ? "K" : ""} ${it.isDriver ? "L" : ""}`}</Text>
          <Text>{`${it.dutyId} ${it.deletion ? "(slettet)" : ""}`}</Text>
          <Box>
            {it.phoneNumber && (
              <CallButton
                phoneNumber={getPhoneNumberWithoutNOPrefix(it.phoneNumber)}
                size="xs"
              />
            )}
          </Box>
          <Box>
            <DayPlanTrackingActionButton dayPlanTracking={it} />
          </Box>
        </Grid>
      );
    })
  ) : (
    <Stack
      gridColumn="1 / -1"
      borderBottomRadius="xs"
      backgroundColor="semantics.colorBackgroundSecondary"
      border="1px solid"
      borderColor="outline.disabled"
      borderTop="none"
    >
      <NoDataMessage
        panelStyle={css`
          border-radius: 0;
          background-color: transparent;
        `}
        reason="Fant ingen tjenestevarslinger som matcher aktivt filter"
      />
    </Stack>
  );
